import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["toggleable"];
  static values = {
    scope: String,    // Optional scope to limit the toggleable targets (e.g., "section1")
    showOn: String,  // The value that should show the target (e.g., "true", "yes", "1")
    hideOn: String   // The value that should hide the target (e.g., "false", "no", "0")
  };

  connect() {
    this.updateVisibility();
  }

  /**
   * Called when a radio button, checkbox, or select changes
   * @param {Event} event - The change event
   */
  toggle(event) {
    // Ignore toggle events with a different scope.
    // Add `data-form-toggle-visibility-scope-param="scopeName"` to the input field to limit the toggle to a specific scope.
    if (this.scopeValue && this.scopeValue !== event.params.scope) return;

    this.updateVisibility();
  }

  /**
   * Updates the visibility of all toggleable targets based on selected radio button value
   */
  updateVisibility() {
    const selectedValue = this.getSelectedValue();

    if (!selectedValue) {
      return;
    }

    this.toggleableTargets.forEach((target) => {
      // Skip targets that have a different scope than the controller's scopeValue.
      // Add `data-form-toggle-visibility-scope="scopeName"` to the target element to limit its visibility to a specific scope.
      if (this.scopeValue && this.scopeValue !== target.dataset.formToggleVisibilityScope) return;

      const shouldShow = this.shouldShowTarget(selectedValue, target);
      this.setVisibility(target, shouldShow);
    });
  }

  /**
   * Gets the currently selected radio button value, "true" if any checkbox
   * in the controller element is checked (for checkbox-group visibility), or dropdown select value.
   * @returns {string|null} The selected value or null if none selected
   */
  getSelectedValue() {
    return this.#getRadioValue() || this.#getCheckboxValue() || this.#getDropdownValue();
  }

  #getRadioValue() {
    let selector = "input[type='radio']";
    if (this.scopeValue) selector += `[data-form-toggle-visibility-scope-param='${this.scopeValue}']`;

    const radioButtons = this.element.querySelectorAll(selector);
    const selectedRadio = Array.from(radioButtons).find(radio => radio.checked);

    return selectedRadio ? selectedRadio.value : null;
  }

  #getCheckboxValue() {
    let selector = "input[type='checkbox']";
    if (this.scopeValue) selector += `[data-form-toggle-visibility-scope-param='${this.scopeValue}']`;

    const checkboxes = this.element.querySelectorAll(selector);

    if (checkboxes.length > 0) {
      return Array.from(checkboxes).some(cb => cb.checked) ? "true" : "false";
    }

    return null;
  }

  #getDropdownValue() {
    let selector = "select";
    if (this.scopeValue) selector += `[data-form-toggle-visibility-scope-param='${this.scopeValue}']`;

    const selectElement = this.element.querySelector(selector);

    return (selectElement && selectElement.value) ? selectElement.value : null;
  }

  /**
   * Determines if a target should be shown based on the selected value
   * @param {string} selectedValue - The currently selected radio value
   * @param {Element} target - The target element to check
   * @returns {boolean} Whether the target should be shown
   */
  shouldShowTarget(selectedValue, target) {
    const targetShowOn = target.dataset.formToggleVisibilityShowOn || this.showOnValue;
    const targetHideOn = target.dataset.formToggleVisibilityHideOn || this.hideOnValue;

    // Priority 1: If showOn is specified, show ONLY when value matches, hide otherwise
    if (targetShowOn) {
      return this.matchesValue(selectedValue, targetShowOn);
    }

    // Priority 2: If hideOn is specified, hide ONLY when value matches, show otherwise
    if (targetHideOn) {
      return !this.matchesValue(selectedValue, targetHideOn);
    }

    // Default behavior: show on "true" (if no showOn or hideOn specified)
    return this.matchesValue(selectedValue, "true");
  }

  /**
   * Checks if a value matches the expected value (case-insensitive)
   * @param {string} value - The value to check
   * @param {string} expected - The expected value
   * @returns {boolean} Whether they match
   */
  matchesValue(value, expected) {
    return value.toString().toLowerCase() === expected.toString().toLowerCase();
  }

  /**
   * Sets the visibility of an element
   * @param {Element} element - The element to show or hide
   * @param {boolean} show - True to show, false to hide
   */
  setVisibility(element, show) {
    element.classList.toggle("hidden", !show);
  }
}
